import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Fai, Plan, TYPE_MARKETING } from '@bytel/bytel-sales';
import { CommercialSeries, ProductService } from '@bytel/checkout';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';

import { CmsService } from '@services/cms.service';
import { MainCartService } from '@services/main-cart.service';

import { Observable, catchError, finalize, map, of, tap } from 'rxjs';

import { BYOU_CATEGORY } from '../../../../../../app/services/mixed-cart.service';
import { CardOfferContainerByouComponent } from '../../card-offer-byou-container /card-offer-container-byou.component';
import { CardOfferContainerComponent } from '../../card-offer-container/card-offer-container.component';
import { SelectChoiceComponent } from '../../select-choice/select-choice.component';

@Component({
    selector: 'checkout-mobile-block-multiple',
    templateUrl: './multiple.component.html',
    styleUrl: 'multiple.component.scss',
    standalone: true,
    imports: [DataCyDirective, SelectChoiceComponent, CardOfferContainerComponent, CardOfferContainerByouComponent],
})
export class MultipleComponent implements OnInit, OnDestroy {
    @Input() public offers: Plan[];
    @Input() public isMobile: boolean;

    @Input() public cartForm: FormGroup;
    @Input() public quoteIndex: number = 1;

    @Output() public selectPlanEvent = new EventEmitter<{ plan: Plan; shouldReset?: boolean }>();

    public defaultValue: boolean = true;
    public isLoading: boolean = false;
    public isCompatiblePromoFoyer: boolean = true;
    public atLeastOneOfferHasPromoHome: boolean = false;
    public atLeastOneBlockablePlan: boolean = false;
    public isByou: boolean = false;

    constructor(
        protected productService: ProductService,
        protected cartService: MainCartService,
        protected cmsService: CmsService,
    ) {}

    public ngOnInit(): void {
        const faiProduct = this.cartService.getQuote().getPrincipalProduct<Fai>('Fai');
        this.isCompatiblePromoFoyer = [
            CommercialSeries.STANDARD,
            CommercialSeries.SMART_TV,
            CommercialSeries.BANQUE,
            CommercialSeries.GAMING,
            CommercialSeries.SERIE_LIMITEE,
            CommercialSeries.TABLETTE,
        ].includes(faiProduct?.data.commercial_series as CommercialSeries);

        this.atLeastOneOfferHasPromoHome = this.offers.some((offer) =>
            offer.promotions.automatic.find((promotion) => promotion.typesMarketing.includes(TYPE_MARKETING.FOYER)),
        );
        this.atLeastOneBlockablePlan = this.offers.some((offer) => offer.data.locked === '1');
        this.isByou = faiProduct.categories.includes(BYOU_CATEGORY);
    }

    public ngOnDestroy(): void {
        this.cartForm.removeControl(`mixed-select-offer${this.quoteIndex}`);
    }

    public trackByPrice(index: number, product: Plan): string {
        return `key-${product.gencode}-${product.prices.final}`;
    }

    public onChoice(value: boolean): any {
        if (value) {
            this.handleSelectTrue().subscribe();
        } else {
            this.handleSelectFalse().subscribe();
        }
    }

    protected handleSelectTrue(): Observable<boolean> {
        if (!this.cartService.getQuote(this.quoteIndex)) {
            this.cartService.addQuote();
        }

        return of(true);
    }

    protected handleSelectFalse(): Observable<boolean> {
        this.isLoading = true;
        return this.cartService.removeQuote(this.quoteIndex).pipe(
            tap(() => {
                this.selectPlanEvent.emit({ plan: null, shouldReset: true });
            }),
            finalize(() => {
                this.isLoading = false;
            }),
            map(() => true),
            catchError(() => of(null)),
        );
    }

    protected initComponent(): void {
        this.initForm();
    }

    protected initForm(): void {
        this.defaultValue = this.getDefaultValue();
    }

    protected getDefaultValue(): boolean {
        return this.cartService.getQuote(this.quoteIndex)?.products.length > 0 ? true : null;
    }
}
